'use client';

import React, { use, useState } from 'react';
import LaporanHeader from '@/components/yayasan/keuangan/LaporanHeader';
import { SchoolUnit, validUnits, getUnitJournal } from '@/lib/data';
import { notFound } from 'next/navigation';
import { Search, FileText, Filter, Receipt } from 'lucide-react';

export default function JurnalLaporanPage({ params }: { params: Promise<{ unit: string }> }) {
    const { unit: initialUnit } = use(params);
    const [activeUnit, setActiveUnit] = useState<SchoolUnit>(initialUnit as SchoolUnit);

    if (!validUnits.includes(initialUnit as SchoolUnit)) {
        notFound();
    }

    const [searchTerm, setSearchTerm] = useState('');
    const unitJournal = getUnitJournal(activeUnit);

    const journals = unitJournal.map(j => ({
        date: j.date,
        id: j.id,
        description: j.description,
        debitAcc: j.debitAccount.split(' (')[0],
        creditAcc: j.creditAccount.split(' (')[0],
        amount: j.amount,
        ref: j.reference
    })).filter(j =>
        j.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        j.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        j.ref.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-24 animate-in fade-in duration-700">
            <LaporanHeader
                title="Jurnal Umum"
                description="Seluruh catatan transaksi keuangan yang terjadi pada unit aktif"
                activeUnit={activeUnit}
                onUnitChange={setActiveUnit}
            />

            {/* Filters & Tools */}
            <div className="bg-surface p-6 rounded-3xl border border-border-main shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="relative w-full md:w-96">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                    <input
                        type="text"
                        placeholder="Cari jurnal, keterangan, atau referansi..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 bg-background border border-border-main rounded-2xl text-xs font-bold outline-none focus:ring-2 focus:ring-primary/20 transition-all shadow-sm"
                    />
                </div>

                <div className="flex items-center gap-3">
                    <div className="px-5 py-3 bg-background border border-border-main rounded-2xl flex items-center gap-3 cursor-pointer hover:bg-surface transition-all">
                        <Filter className="w-4 h-4 text-primary" />
                        <span className="text-[10px] font-bold uppercase tracking-widest text-foreground">Semua Jenis Transaksi</span>
                    </div>
                </div>
            </div>

            {/* Journal Table */}
            <div className="bg-surface rounded-4xl border border-border-main shadow-sm overflow-hidden animate-in slide-in-from-bottom-4 duration-700 flex flex-col">
                <div className="p-7 border-b border-border-main flex items-center justify-between bg-primary-soft/10">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-primary text-white rounded-xl shadow-lg shadow-primary/10">
                            <FileText className="w-5 h-5" />
                        </div>
                        <h3 className="text-xl font-bold">Data Jurnal Komprehensif</h3>
                    </div>
                    <span className="text-[10px] font-bold text-muted uppercase tracking-widest bg-white/50 px-4 py-1.5 rounded-full border border-border-main">
                        Read Only Access
                    </span>
                </div>

                <div className="overflow-x-auto text-sm font-medium">
                    <table className="w-full text-left">
                        <thead className="bg-background/50 border-b border-border-main">
                            <tr>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Waktu & Ref</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Keterangan</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Akun Debit</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Akun Kredit</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-right">Nominal</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-center">Referansi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-main">
                            {journals.map((j: any) => (
                                <tr key={j.id} className="hover:bg-primary-soft/5 transition-colors group">
                                    <td className="px-6 py-6 min-w-[150px]">
                                        <div className="flex flex-col gap-1">
                                            <span className="font-bold text-foreground">{j.date}</span>
                                            <span className="text-[10px] text-primary font-bold uppercase tracking-widest bg-primary-soft px-2 py-0.5 rounded w-fit">{j.id}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-6 max-w-sm">
                                        <span className="text-secondary font-semibold leading-relaxed group-hover:text-foreground transition-colors">{j.description}</span>
                                    </td>
                                    <td className="px-6 py-6">
                                        <div className="flex items-center gap-2">
                                            <div className="w-1.5 h-1.5 rounded-full bg-success opacity-70" />
                                            <span className="text-foreground font-bold">{j.debitAcc}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-6">
                                        <div className="flex items-center gap-2 pl-4 border-l border-dashed border-border-main">
                                            <div className="w-1.5 h-1.5 rounded-full bg-error opacity-70" />
                                            <span className="text-secondary italic">{j.creditAcc}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-6 text-right">
                                        <span className="font-bold text-lg tabular-nums text-foreground">{j.amount}</span>
                                    </td>
                                    <td className="px-6 py-6 text-center">
                                        <span className="text-[10px] font-bold text-muted uppercase tracking-widest bg-background px-3 py-1.5 rounded-lg border border-border-main flex items-center justify-center gap-2 w-fit mx-auto">
                                            <Receipt className="w-3 h-3" />
                                            {j.ref}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="p-6 bg-background/50 border-t border-border-main flex items-center justify-between">
                    <p className="text-xs text-muted font-bold uppercase tracking-widest">Menampilkan {journals.length} baris jurnal</p>
                    <div className="flex items-center gap-2">
                        <button className="px-4 py-2 bg-surface border border-border-main rounded-xl text-[10px] font-bold text-muted">Sebelumnya</button>
                        <button className="px-4 py-2 bg-primary text-white rounded-xl text-[10px] font-bold shadow-lg shadow-primary/20">Berikutnya</button>
                    </div>
                </div>
            </div>
        </main>
    );
}
